import type {Emoji as MarkdownEmoji} from '@github-ui/markdown-editor'
import {getUnicodeEmojiContent, isUnicodeEmoji} from '@github-ui/markdown-editor/suggestions/use-emoji-suggestions'
import {SmileyIcon} from '@primer/octicons-react'
import {IconButton, SelectPanel, type SelectPanelItemInput} from '@primer/react'
import {useVirtualizer} from '@tanstack/react-virtual'
import {useCallback, useMemo, useState} from 'react'

import {useStatusEmojiQuery} from '../hooks/use-status-emoji-query'
import styles from '../UserStatusDialog.module.css'

const DEFAULT_VIRTUAL_ITEM_HEIGHT = 32

export interface EmojiPickerProps {
  /** A user preference setting that controls the tone of Emojis */
  tone: number
  /** The current emoji value */
  value?: SelectPanelItemInput
  /** Callback fired when the emoji value changes */
  setValue: (value: SelectPanelItemInput | undefined) => void
}

// Create stable visual functions to avoid recreating on every render
const createEmojiVisual = (emoji: MarkdownEmoji, tone: number) => {
  if (isUnicodeEmoji(emoji)) {
    const EmojiVisual = () => getUnicodeEmojiContent(emoji, tone)
    EmojiVisual.displayName = `UnicodeEmoji_${emoji.name}`
    return EmojiVisual
  }
  // For custom emojis, return a stable function that renders the img
  const {url, name} = emoji
  const EmojiVisual = () => <img src={url} alt={`${name} emoji`} height="16" width="16" />
  EmojiVisual.displayName = `CustomEmoji_${name}`
  return EmojiVisual
}

const EmojiPicker = (props: EmojiPickerProps) => {
  const {tone, value, setValue} = props
  const [open, setOpen] = useState(false)
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null)
  const [_filter, setFilter] = useState<string | undefined>(undefined)
  const emojisQuery = useStatusEmojiQuery()

  const items: SelectPanelItemInput[] = useMemo(
    () =>
      emojisQuery.isPending || !emojisQuery.data
        ? []
        : emojisQuery.data.map(emoji => ({
            text: emoji.name,
            id: emoji.name,
            leadingVisual: createEmojiVisual(emoji, tone),
          })),
    [emojisQuery.data, emojisQuery.isPending, tone],
  )

  const filteredItems: SelectPanelItemInput[] = useMemo(() => {
    if (!_filter || _filter.trim() === '') {
      return items
    }
    const lowerFilter = _filter.toLowerCase()
    return items.filter(item => item.text?.toLowerCase().includes(lowerFilter))
  }, [_filter, items])

  const virtualizer = useVirtualizer({
    count: filteredItems.length,
    getScrollElement: () => scrollContainer ?? null,
    estimateSize: () => DEFAULT_VIRTUAL_ITEM_HEIGHT,
    overscan: 10,
    getItemKey: index => filteredItems[index]?.id ?? index,
    measureElement: el => {
      return (el as HTMLElement).scrollHeight
    },
  })

  return (
    <>
      <input
        type="hidden"
        name="emoji"
        value={value?.id ? `:${value.id}:` : ''}
        data-testid="user-status-emoji-input"
      />
      <SelectPanel
        title="Select an Emoji"
        initialLoadingType="spinner"
        loading={emojisQuery.isPending}
        id="user-status-emoji-picker-button"
        renderAnchor={({children, ...anchorProps}) => (
          <IconButton
            {...anchorProps}
            aria-label={value?.text ? `Select an emoji: :${value?.text}:` : 'Select an emoji'}
            aria-haspopup="dialog"
            icon={value?.leadingVisual ?? SmileyIcon}
            data-testid="user-status-emoji-picker-button"
            className={styles.emojiPickerButton}
          />
        )}
        open={open}
        onOpenChange={setOpen}
        placeholderText="Filter Emojis"
        items={virtualizer.getVirtualItems().map(virtualItem => {
          const item = filteredItems[virtualItem.index]

          return {
            ...item,
            key: item?.id,
            'data-index': virtualItem.index,
            style: {
              position: 'absolute' as const,
              top: 0,
              left: 0,
              width: 'calc(100% - 16px)',
              height: `${virtualItem.size}px`,
              transform: `translateY(${virtualItem.start}px)`,
            },
            ref: (node: Element | null) => {
              if (node && node.getAttribute('data-index')) {
                virtualizer.measureElement(node)
              }
            },
          }
        })}
        selected={value}
        onSelectedChange={setValue}
        onFilterChange={setFilter}
        focusOutBehavior="stop"
        onActiveDescendantChanged={useCallback(
          (newActiveDescendant: HTMLElement | undefined) => {
            const index = newActiveDescendant?.getAttribute('data-index')
            const range = virtualizer.range
            if (index && range && (Number(index) < range.startIndex || Number(index) >= range.endIndex)) {
              virtualizer.scrollToIndex(Number(index), {align: 'auto'})
            }
          },
          [virtualizer],
        )}
        scrollContainerRef={node => setScrollContainer(node)}
        actionListProps={{
          style: {
            maxHeight: 300,
            height: virtualizer.getTotalSize(),
            width: '100%',
            position: 'relative' as const,
          },
        }}
        secondaryAction={
          <SelectPanel.SecondaryActionButton
            onClick={() => {
              setValue(undefined)
              setOpen(false)
            }}
            data-testid="user-status-emoji-clear-button"
          >
            Clear emoji
          </SelectPanel.SecondaryActionButton>
        }
      />
    </>
  )
}

export default EmojiPicker

try{ EmojiPicker.displayName ||= 'EmojiPicker' } catch {}